import React, { useEffect, useRef, useState } from 'react';
import {
	Wrapper,
	Rail,
	Stop,
	StopButton,
	Panel,
	Position,
	Detail,
	Company,
	InfoWrapper,
	SkillsWrapper,
	Resume,
	ResumeWrapper,
} from './styles';
import { Content } from '../../shared/styles';
import { jobs } from '../../assets/jobInfo.js';
import resume from '../../assets/resume.pdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

// oldest on the left, newest on the right
const timeline = [...jobs].reverse();

const centerStop = (r, stops, i, behavior = 'smooth') => {
	const stop = stops[Math.max(0, Math.min(i, stops.length - 1))];
	r.scrollTo({
		left: stop.offsetLeft - (r.clientWidth - stop.offsetWidth) / 2,
		behavior,
	});
};

const Experience = () => {
	const [active, setActive] = useState(timeline.length - 1);
	const rail = useRef(null);
	const stops = useRef([]);

	// Center stop i in the rail; the scroll handler then marks it active.
	const focusJob = (i, behavior) => centerStop(rail.current, stops.current, i, behavior);

	useEffect(() => {
		const r = rail.current;
		// Active job = the stop closest to the rail's center.
		const onScroll = () => {
			const center = r.scrollLeft + r.clientWidth / 2;
			let best = 0;
			stops.current.forEach((s, i) => {
				const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - center);
				const bestS = stops.current[best];
				if (d < Math.abs(bestS.offsetLeft + bestS.offsetWidth / 2 - center))
					best = i;
			});
			setActive(best);
		};
		// Vertical mouse wheel anywhere on the page steps one job at a time; locked briefly so a trackpad fling doesn't skip several.
		let locked = false;
		const onWheel = (e) => {
			if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
			const current = stops.current.findIndex(
				(s) => s.dataset.active === 'true'
			);
			const next = current + Math.sign(e.deltaY);
			if (next < 0 || next >= timeline.length) return; // let the page scroll at the ends
			e.preventDefault();
			if (locked) return;
			locked = true;
			setTimeout(() => (locked = false), 450);
			centerStop(r, stops.current, next);
		};
		r.scrollLeft = r.scrollWidth;
		onScroll();
		r.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('wheel', onWheel, { passive: false });
		window.addEventListener('resize', onScroll);
		return () => {
			r.removeEventListener('scroll', onScroll);
			window.removeEventListener('wheel', onWheel);
			window.removeEventListener('resize', onScroll);
		};
	}, []);

	const onKeyDown = (e) => {
		const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
		const next = active + (step || 0);
		if (!step || next < 0 || next >= timeline.length) return;
		e.preventDefault();
		focusJob(next);
		stops.current[next].querySelector('button').focus({ preventScroll: true });
	};

	const job = timeline[active];

	return (
		<Content>
			<Wrapper>
				<Panel key={active} aria-live='polite'>
					<Position>{job.Position}</Position>
					<Company>{job.Company}</Company>
					<InfoWrapper>
						<Detail>{job.Location}</Detail>
					</InfoWrapper>
					<SkillsWrapper>
						{job.Skills.map((skill) => (
							<Detail key={skill} size={'small'}>
								{skill}
							</Detail>
						))}
					</SkillsWrapper>
				</Panel>

				<Rail ref={rail} onKeyDown={onKeyDown}>
					{timeline.map((j, i) => (
						<Stop
							key={j.Company}
							ref={(el) => (stops.current[i] = el)}
							data-active={i === active}
							$active={i === active}
							$order={i}
						>
							<StopButton
								onClick={() => focusJob(i)}
								aria-current={i === active}
							>
								<Detail size={'small'}>{j.Timeline}</Detail>
								<Company>{j.Company}</Company>
							</StopButton>
						</Stop>
					))}
				</Rail>

				<ResumeWrapper>
					<Resume href={resume} target='_blank' rel='noopener noreferrer'>
						<b>
							Resume <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
						</b>
					</Resume>
				</ResumeWrapper>
			</Wrapper>
		</Content>
	);
};

export default Experience;
